import { CheckCircle2, ChevronRight, Filter, Plus, SearchX } from "lucide-react"
import * as React from "react"

import { Skeleton } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { SearchInput } from "@/components/ui/search-input"
import { cn } from "@/lib/utils"

import { FindingRecord } from "./finding-record"
import {
  documentPlacementFindings,
  groupFindings,
  matchesSavedView,
  matchesSearch,
  undecidedCount,
  type Finding,
  type FindingId,
  type SavedViewId,
} from "../data/review-data"

/**
 * The right rail. Grouped by KPI, sorted by number within each group. Owns
 * the search + saved-view filter. Selection and expansion are lifted so
 * the PDF overlay and this rail share one active-finding pointer.
 *
 * The empty state distinguishes "no findings at all" (rare — worth a
 * side-by-side confirmation) from "your filters hid them" (common — offer
 * a Clear button). Different problems, different next steps.
 */
export interface FindingsRailProps {
  findings: Finding[]
  loading: boolean
  activeFindingId: FindingId | null
  expandedFindingId: FindingId | null
  editingFindingId: FindingId | null
  canDispose: boolean
  onSelect: (id: FindingId) => void
  onToggleExpand: (id: FindingId) => void
  onEnterEdit: (id: FindingId) => void
  onCancelEdit: () => void
  onSave: (
    id: FindingId,
    patch: { claim: string; reasoning: string | null; remediation: string | null },
  ) => void
  onConfirm: (id: FindingId) => void
  onDismiss: (id: FindingId) => void
  onSendToSme: (id: FindingId) => void
  onMarkCorrected: (id: FindingId) => void
  onOpenComments: (id: FindingId) => void
  onAddFinding: () => void
  searchRef?: React.RefObject<HTMLInputElement | null>
}

const SAVED_VIEWS: Array<{ id: SavedViewId; label: string }> = [
  { id: "all", label: "All" },
  { id: "needs_decision", label: "Needs a decision" },
  { id: "blocking_report", label: "Blocking the report" },
  { id: "not_addressed", label: "Not addressed" },
  { id: "client_disputed", label: "Client disputed" },
]

export function FindingsRail({
  findings,
  loading,
  activeFindingId,
  expandedFindingId,
  editingFindingId,
  canDispose,
  onSelect,
  onToggleExpand,
  onEnterEdit,
  onCancelEdit,
  onSave,
  onConfirm,
  onDismiss,
  onSendToSme,
  onMarkCorrected,
  onOpenComments,
  onAddFinding,
  searchRef,
}: FindingsRailProps): JSX.Element {
  const [search, setSearch] = React.useState("")
  const [savedView, setSavedView] = React.useState<SavedViewId>("all")

  const filtered = React.useMemo(
    () => findings.filter((f) => matchesSavedView(savedView, f) && matchesSearch(f, search)),
    [findings, savedView, search],
  )

  const groups = React.useMemo(() => groupFindings(filtered), [filtered])
  const documentOnly = React.useMemo(() => documentPlacementFindings(filtered), [filtered])
  const undecided = React.useMemo(() => undecidedCount(findings), [findings])

  const isFiltered = search.length > 0 || savedView !== "all"

  return (
    <aside
      aria-label="Findings"
      className="flex h-full min-h-0 flex-col overflow-hidden bg-surface"
    >
      <div className="flex items-baseline justify-between gap-3 border-b border-border-soft px-4 py-3">
        <div className="flex items-baseline gap-2">
          <h2 className="text-body font-semibold text-text">
            {loading ? (
              <Skeleton className="inline-block h-4 w-24 align-baseline" />
            ) : (
              `${findings.length} findings`
            )}
          </h2>
          {!loading && undecided > 0 && (
            <span className="text-caption text-text-3">
              · {undecided} undecided
            </span>
          )}
        </div>
        <Button
          size="sm"
          variant="default"
          leadingIcon={<Plus />}
          onClick={onAddFinding}
        >
          Add finding
        </Button>
      </div>

      <div className="grid gap-2 border-b border-border-soft px-4 py-3">
        <SearchInput
          ref={searchRef}
          placeholder="Search rule, group, or claim"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          aria-label="Search findings"
        />
        <div className="flex flex-wrap items-center gap-1">
          <Filter className="mr-1 size-3 text-text-3" aria-hidden="true" />
          {SAVED_VIEWS.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => setSavedView(v.id)}
              aria-pressed={savedView === v.id}
              className={cn(
                "rounded-sm px-2 py-1 text-caption font-medium transition-colors duration-120 ease-out",
                savedView === v.id
                  ? "bg-brand-tint text-brand ring-1 ring-inset ring-brand/30"
                  : "text-text-2 hover:bg-surface-sunken",
              )}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto px-3 py-3">
        {loading ? (
          <SkeletonList />
        ) : filtered.length === 0 ? (
          <div className="flex h-full items-center justify-center px-4 py-8">
            <EmptyRailState
              isFiltered={isFiltered}
              onClear={() => {
                setSearch("")
                setSavedView("all")
              }}
              onAddFinding={onAddFinding}
            />
          </div>
        ) : (
          <div className="space-y-6">
            {groups.map(({ group, items }) => (
              <RailGroup
                key={group.id}
                eyebrow={`Group ${group.ordinal}`}
                label={group.label}
                count={items.length}
              >
                <ul className="space-y-1.5">
                  {items.map((f) => (
                    <FindingRecord
                      key={f.id}
                      finding={f}
                      selected={activeFindingId === f.id}
                      expanded={expandedFindingId === f.id}
                      editing={editingFindingId === f.id}
                      canDispose={canDispose}
                      onSelect={() => onSelect(f.id)}
                      onToggleExpand={() => onToggleExpand(f.id)}
                      onEnterEdit={() => onEnterEdit(f.id)}
                      onCancelEdit={onCancelEdit}
                      onSave={(patch) => onSave(f.id, patch)}
                      onConfirm={() => onConfirm(f.id)}
                      onDismiss={() => onDismiss(f.id)}
                      onSendToSme={() => onSendToSme(f.id)}
                      onMarkCorrected={() => onMarkCorrected(f.id)}
                      onOpenComments={() => onOpenComments(f.id)}
                    />
                  ))}
                </ul>
              </RailGroup>
            ))}
            {!isFiltered && documentOnly.length > 0 && (
              <RailGroup
                eyebrow="Elsewhere"
                label="Not on the page"
                count={documentOnly.length}
                description="The divergence is either absent from the label, or a property of the whole document."
              />
            )}
          </div>
        )}
      </div>
    </aside>
  )
}

function SkeletonList(): JSX.Element {
  return (
    <div className="space-y-3">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="space-y-2 rounded-md border border-border-soft p-3">
          <div className="flex items-center gap-2">
            <Skeleton className="size-5 rounded-full" />
            <Skeleton className="h-3 w-12" />
            <Skeleton className="h-3 w-14" />
          </div>
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-4/5" />
        </div>
      ))}
    </div>
  )
}

function EmptyRailState({
  isFiltered,
  onClear,
  onAddFinding,
}: {
  isFiltered: boolean
  onClear: () => void
  onAddFinding: () => void
}): JSX.Element {
  if (isFiltered) {
    return (
      <EmptyPanel
        tone="neutral"
        icon={<SearchX className="size-5" aria-hidden="true" />}
        eyebrow="Filtered view"
        title="Nothing matches"
        body="The findings are still here — the filters are hiding them."
        action={
          <Button size="sm" onClick={onClear}>
            Clear filters
          </Button>
        }
      />
    )
  }
  return (
    <EmptyPanel
      tone="master"
      icon={<CheckCircle2 className="size-5" aria-hidden="true" />}
      eyebrow="Clean pass"
      title="No divergences found"
      body="Every checked rule passed against the governing master. Worth a quick side-by-side confirmation before you send — a clean review is unusual enough to earn the extra minute."
      action={
        <Button size="sm" variant="primary" leadingIcon={<Plus />} onClick={onAddFinding}>
          Add a finding
        </Button>
      }
    />
  )
}

function EmptyPanel({
  tone,
  icon,
  eyebrow,
  title,
  body,
  action,
}: {
  tone: "master" | "neutral"
  icon: React.ReactNode
  eyebrow: string
  title: string
  body: string
  action: React.ReactNode
}): JSX.Element {
  return (
    <div className="flex w-full max-w-[36ch] flex-col items-center gap-3 text-center">
      <span
        className={cn(
          "grid size-9 place-items-center rounded-full",
          tone === "master"
            ? "bg-master-tint text-master"
            : "bg-surface-sunken text-text-3",
        )}
      >
        {icon}
      </span>
      <span
        className={cn(
          "font-id text-[10px] uppercase tracking-[0.14em]",
          tone === "master" ? "text-master" : "text-text-3",
        )}
      >
        {eyebrow}
      </span>
      <h3 className="text-subtitle font-semibold tracking-tight text-text">
        {title}
      </h3>
      <p className="text-body-sm leading-[1.55] text-text-2">{body}</p>
      <div className="mt-2">{action}</div>
    </div>
  )
}

/**
 * A rail section header. Landmark treatment: the eyebrow ("Group 3")
 * carries the ordinal, the label ("Text review") carries the meaning,
 * and the count sits as a subtle chip.
 *
 * Groups with children collapse — reviewers with 30+ findings need to
 * fold away sections they've already worked. Collapse defaults to open
 * so the first-time reader sees everything; state is local (not
 * persisted) because "which groups I had folded last session" is not
 * something a reviewer thinks about — they think about *this* pass.
 */
function RailGroup({
  eyebrow,
  label,
  count,
  description,
  children,
}: {
  eyebrow: string
  label: string
  count: number
  description?: string
  children?: React.ReactNode
}): JSX.Element {
  const collapsible = Boolean(children)
  const [open, setOpen] = React.useState(true)
  const bodyId = React.useId()

  const Header = (
    <>
      {collapsible && (
        <ChevronRight
          aria-hidden="true"
          className={cn(
            "size-3 shrink-0 text-text-3 transition-transform duration-180 ease-out",
            open && "rotate-90",
          )}
        />
      )}
      <span className="font-id text-eyebrow uppercase tracking-[0.12em] text-text-3">
        {eyebrow}
      </span>
      <span className="font-id text-eyebrow uppercase tracking-[0.12em] font-semibold text-text-2">
        {label}
      </span>
      <span className="text-caption tabular-nums text-text-3">({count})</span>
    </>
  )

  return (
    <section aria-label={label}>
      {collapsible ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={bodyId}
          className="mb-2 flex w-full items-center gap-2 rounded-sm px-0.5 py-0.5 text-left transition-colors duration-120 ease-out hover:bg-surface-sunken/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]"
        >
          {Header}
        </button>
      ) : (
        <div className="mb-2 flex items-baseline gap-2 px-0.5">{Header}</div>
      )}
      {description && (
        <p className="mb-2 px-0.5 text-caption leading-normal text-text-3">
          {description}
        </p>
      )}
      {collapsible && (
        <div id={bodyId} hidden={!open}>
          {children}
        </div>
      )}
    </section>
  )
}
